interface TagRule {
  regex: RegExp
  replace: string | ((...args: string[]) => string)
}

const TAG_RULES: TagRule[] = [
  // Behavior flags - strip from display
  { regex: /\[pill="[^"]*"\]/g, replace: '' },
  { regex: /\[check="[^"]*"\]/g, replace: '' },

  // Formatting
  { regex: /\[b\](.*?)\[\/b\]/gs, replace: '<strong>$1</strong>' },
  { regex: /\[i\](.*?)\[\/i\]/gs, replace: '<em>$1</em>' },

  // Links
  {
    regex: /\[link=(.*?)\](.*?)\[\/link\]/gs,
    replace: '<a href="$1" target="_blank" rel="noopener noreferrer" class="guidance-link">$2</a>',
  },

  // Badges
  {
    regex: /\[badge=(safe|warn|danger|verified)\](.*?)\[\/badge\]/gs,
    replace: '<span class="guidance-badge guidance-badge-$1">$2</span>',
  },

  // Contextual spans
  { regex: /\[tip\](.*?)\[\/tip\]/gs, replace: '<span class="guidance-tip">$1</span>' },
  { regex: /\[warn\](.*?)\[\/warn\]/gs, replace: '<span class="guidance-warn-accent">$1</span>' },

  // Line break
  { regex: /\[br\]/g, replace: '<br />' },
]

/**
 * Parse bracket tags in admin-authored text into HTML.
 * Applies string replacements first, then tag transforms.
 */
export function parseGuidanceTags(
  raw: string,
  replacements?: Record<string, string>
): string {
  if (!raw) return ''

  let result = raw

  // Apply replacements first
  if (replacements) {
    for (const [key, value] of Object.entries(replacements)) {
      result = result.replaceAll(key, value)
    }
  }

  // Apply tag transforms
  for (const { regex, replace } of TAG_RULES) {
    result = result.replace(regex, replace as string)
  }

  return result
}

/**
 * Extract the first [pill="..."] tag value from admin-authored text.
 * Used as badge/pill text in GuidanceHelper, replacing hardcoded labels.
 * Applies replacements first so dynamic values are resolved.
 */
export function extractPillTag(
  raw: string,
  replacements?: Record<string, string>
): string {
  if (!raw) return ''

  let text = raw
  if (replacements) {
    for (const [key, value] of Object.entries(replacements)) {
      text = text.replaceAll(key, value)
    }
  }

  const m = /\[pill="([^"]*)"\]/.exec(text)
  return m ? m[1] : ''
}

/**
 * Extract checkbox labels from [check="..."] tags in admin-authored text.
 * Applies replacements first so dynamic values are resolved in labels.
 */
export function extractCheckTags(
  raw: string,
  replacements?: Record<string, string>
): string[] {
  if (!raw) return []

  let text = raw
  if (replacements) {
    for (const [key, value] of Object.entries(replacements)) {
      text = text.replaceAll(key, value)
    }
  }

  const matches: string[] = []
  const re = /\[check="([^"]*)"\]/g
  let m
  while ((m = re.exec(text)) !== null) matches.push(m[1])
  return matches
}
